import { AI_CHATBOT_CONFIG } from '../../config'

/**
 * Runaway-loop guard.
 *
 * CLIENT-SIDE UX SAFEGUARD — NOT a security control.
 * PISA permissions are the authoritative rate limit. This guard exists purely
 * to catch accidental LLM tool-call loops that would burn through user trust
 * (e.g. "create tickets for all 50 companies" followed by an unbounded loop).
 *
 * Uses a sliding-window counter per category. Each category tracks timestamps
 * of recent calls; stale entries are evicted before each check.
 */

type RateCategory = 'creates' | 'updates' | 'searches'

const timestamps: Record<RateCategory, number[]> = {
  creates: [],
  updates: [],
  searches: [],
}

function maxForCategory(category: RateCategory): number {
  const guard = AI_CHATBOT_CONFIG.runawayLoopGuard
  if (category === 'creates') return guard.createsPerWindow
  if (category === 'updates') return guard.updatesPerWindow
  return 20 // searches
}

/**
 * Check whether the given category has headroom for one more call.
 * Returns `{ allowed: true }` when under the limit, or
 * `{ allowed: false, message }` when the limit is reached.
 *
 * Call `recordCall(category)` immediately after receiving `allowed: true`
 * to register the call in the window.
 */
export function checkRateLimit(category: RateCategory): {
  allowed: boolean
  message?: string
} {
  const { windowMs } = AI_CHATBOT_CONFIG.runawayLoopGuard
  const now = Date.now()
  const cutoff = now - windowMs

  // Evict stale timestamps
  timestamps[category] = timestamps[category].filter((t) => t > cutoff)

  const max = maxForCategory(category)
  if (timestamps[category].length >= max) {
    return {
      allowed: false,
      message: `I've made ${max} ${category} in the last minute. Let's slow down — want to review what we've done so far?`,
    }
  }

  return { allowed: true }
}

/**
 * Record a call for the given category. Always call this after a successful
 * PISA write — NOT on error responses.
 */
export function recordCall(category: RateCategory): void {
  timestamps[category].push(Date.now())
}

/**
 * Reset all rate-limit windows (e.g. on conversation reset or user confirmation
 * of a review card). Exposed for testing and UX reset flows.
 */
export function resetRateLimits(): void {
  timestamps.creates = []
  timestamps.updates = []
  timestamps.searches = []
}
